import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  compareDescByDate,
  formatChinaDate,
  formatChinaDateTime,
} from '../../utils/date';

type StatState = {
  members: number;
  news: number;
  publications: number;
  openRecruitments: number;
  pendingMessages: number;
};

type SimpleNews = { _id: string; title: string; publishedAt?: string };
type SimpleMessage = { _id: string; name: string; createdAt?: string; handled: boolean };

const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<StatState>({
    members: 0,
    news: 0,
    publications: 0,
    openRecruitments: 0,
    pendingMessages: 0,
  });
  const [latestNews, setLatestNews] = useState<SimpleNews[]>([]);
  const [latestMessages, setLatestMessages] = useState<SimpleMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const [
          membersRes,
          newsRes,
          pubsRes,
          recruitmentRes,
          messagesRes,
        ] = await Promise.allSettled([
          api.get('/members'),
          api.get('/news'),
          api.get('/publications'),
          api.get('/recruitment-positions/all'),
          api.get('/contact-messages'),
        ]);

        const members =
          membersRes.status === 'fulfilled' && Array.isArray(membersRes.value.data)
            ? membersRes.value.data.length
            : 0;
        const newsList =
          newsRes.status === 'fulfilled' && Array.isArray(newsRes.value.data)
            ? newsRes.value.data
            : [];
        const publications =
          pubsRes.status === 'fulfilled' && Array.isArray(pubsRes.value.data)
            ? pubsRes.value.data.length
            : 0;
        const recruitmentsRaw =
          recruitmentRes.status === 'fulfilled' &&
          Array.isArray(recruitmentRes.value.data)
            ? recruitmentRes.value.data
            : [];
        const messagesRaw =
          messagesRes.status === 'fulfilled' &&
          Array.isArray(messagesRes.value.data)
            ? messagesRes.value.data
            : [];

        const openRecruitments = recruitmentsRaw.filter(
          (r: any) => r.status === 'open'
        ).length;
        const pendingMessages = messagesRaw.filter(
          (m: any) => !m.handled
        ).length;

        setStats({
          members,
          news: newsList.length,
          publications,
          openRecruitments,
          pendingMessages,
        });

        setLatestNews(
          newsList
            .slice()
            .sort(
              (a: any, b: any) =>
                compareDescByDate(a.publishedAt, b.publishedAt)
            )
            .slice(0, 3)
            .map((n: any) => ({
              _id: n._id,
              title: n.title,
              publishedAt: n.publishedAt,
            }))
        );

        setLatestMessages(
          messagesRaw
            .slice()
            .sort(
              (a: any, b: any) =>
                compareDescByDate(a.createdAt, b.createdAt)
            )
            .slice(0, 3)
            .map((m: any) => ({
              _id: m._id,
              name: m.name,
              createdAt: m.createdAt,
              handled: m.handled,
            }))
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Dashboard
        </h1>
        <button
          onClick={() => navigate('/')}
          className="rounded-full border border-neutral-300 px-3 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
        >
          View frontend home
        </button>
      </div>

      <p className="mb-6 text-xs text-neutral-600">
        Quick overview of current site content and pending tasks. Click a card to open its management page.
      </p>

      {/* Key metrics */}
      <section className="mb-8 grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        <button
          type="button"
          onClick={() => navigate('/admin/members')}
          className="flex flex-col rounded-lg border border-neutral-200 bg-white p-3 text-left shadow-subtle hover:border-brand-red/60"
        >
          <span className="text-[11px] text-neutral-500">Members</span>
          <span className="mt-1 text-2xl font-semibold text-neutral-900">
            {stats.members}
          </span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin/news')}
          className="flex flex-col rounded-lg border border-neutral-200 bg-white p-3 text-left shadow-subtle hover:border-brand-red/60"
        >
          <span className="text-[11px] text-neutral-500">News Items</span>
          <span className="mt-1 text-2xl font-semibold text-neutral-900">
            {stats.news}
          </span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin/publications')}
          className="flex flex-col rounded-lg border border-neutral-200 bg-white p-3 text-left shadow-subtle hover:border-brand-red/60"
        >
          <span className="text-[11px] text-neutral-500">Publications</span>
          <span className="mt-1 text-2xl font-semibold text-neutral-900">
            {stats.publications}
          </span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin/recruitment')}
          className="flex flex-col rounded-lg border border-neutral-200 bg-white p-3 text-left shadow-subtle hover:border-brand-red/60"
        >
          <span className="text-[11px] text-neutral-500">Open Positions</span>
          <span className="mt-1 text-2xl font-semibold text-neutral-900">
            {stats.openRecruitments}
          </span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin/messages')}
          className="flex flex-col rounded-lg border border-neutral-200 bg-white p-3 text-left shadow-subtle hover:border-brand-red/60"
        >
          <span className="text-[11px] text-neutral-500">Pending Messages</span>
          <span className="mt-1 text-2xl font-semibold text-neutral-900">
            {stats.pendingMessages}
          </span>
        </button>
      </section>

      {/* Latest updates & message preview */}
      <section className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)]">
        <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-subtle">
          <div className="mb-3 flex items-center justify-between text-xs">
            <span className="font-semibold text-neutral-800">Latest News</span>
            <Link
              to="/admin/news"
              className="text-[11px] font-semibold text-brand-red hover:underline"
            >
              Manage News
            </Link>
          </div>
          {loading ? (
            <p className="text-xs text-neutral-500">Loading...</p>
          ) : latestNews.length === 0 ? (
            <p className="text-xs text-neutral-500">No news content yet.</p>
          ) : (
            <ul className="space-y-2 text-xs">
              {latestNews.map((n) => (
                <li key={n._id} className="flex items-baseline justify-between gap-3">
                  <div className="flex-1">
                    <Link
                      to={`/admin/news`}
                      className="line-clamp-2 font-medium text-neutral-900 hover:text-brand-red"
                    >
                      {stripHtml(n.title)}
                    </Link>
                  </div>
                  <span className="whitespace-nowrap text-[11px] text-neutral-500">
                    {n.publishedAt ? formatChinaDate(n.publishedAt) : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-subtle">
          <div className="mb-3 flex items-center justify-between text-xs">
            <span className="font-semibold text-neutral-800">Latest Messages</span>
            <Link
              to="/admin/messages"
              className="text-[11px] font-semibold text-brand-red hover:underline"
            >
              View all
            </Link>
          </div>
          {loading ? (
            <p className="text-xs text-neutral-500">Loading...</p>
          ) : latestMessages.length === 0 ? (
            <p className="text-xs text-neutral-500">No messages yet.</p>
          ) : (
            <ul className="space-y-2 text-xs">
              {latestMessages.map((m) => (
                <li key={m._id} className="flex items-baseline justify-between gap-3">
                  <div className="flex-1">
                    <span className="font-medium text-neutral-900">
                      {m.name}
                    </span>
                    <span className="ml-2 text-[11px] text-neutral-500">
                      {m.handled ? 'Handled' : 'Pending'}
                    </span>
                  </div>
                  <span className="whitespace-nowrap text-[11px] text-neutral-500">
                    {m.createdAt ? formatChinaDateTime(m.createdAt) : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;

